import { configuration, SameAsNsiMode } from "configuration"
import { MappingResponseItem } from "model/ClassifierModel"
import { MappingResult } from "type/mapping/result"
import { SimilarNomenclature } from "type/mapping/similarNomenclature"
import { WithId, WithStrId } from "type/withId"

export const getMappedNomenclature = (
  mapping: WithId<MappingResult>,
): MappingResponseItem | undefined => {
  const result = mapping.result

  const mappingsList = result?.mappings
  if (mappingsList && mappingsList.length > 0) {
    return mappingsList[0]
  }

  return undefined
}

export const isMappedNomenclatureValid = (mapping: WithId<MappingResult>): boolean => {
  const mappedNomenclature = getMappedNomenclature(mapping)
  const isMappedNomenclatureExists = !!mappedNomenclature

  return isMappedNomenclatureExists
}

// Вариант "нет в НСИ": сметчик подтверждает, что подходящей позиции НСИ нет.
// id "-1" — как у варианта "В НСИ нет подходящего варианта" в окне выбора из
// НСИ (NSIViewer), чтобы оба способа давали одно и то же; в BI такая позиция
// уходит с пустым nomenclature_guid.
export const NOT_IN_NSI_NOMENCLATURE: WithStrId<SimilarNomenclature> = {
  id: "-1",
  name: "Нет в НСИ",
  material_code: null,
}

// String(): в старых проверках id сохранён числом -1, бэк сравнивает так же
export const isNotInNSI = (nomenclature?: WithStrId<SimilarNomenclature>): boolean =>
  !!nomenclature && String(nomenclature.id) === NOT_IN_NSI_NOMENCLATURE.id

// Есть ли у строки найденный материал. Пустое имя — LLM нашла группу, но не
// выбрала материал. У сметы ещё и без кода НСИ: LLM иногда "выбирает"
// название, которого в НСИ нет — такое нельзя показывать найденным материалом
export const hasMappedNomenclature = (
  mapping: WithId<MappingResult>,
  isCodeRequired?: boolean,
): boolean => {
  const mappedNomenclature = getMappedNomenclature(mapping)
  return (
    !!mappedNomenclature?.nomenclature?.trim() &&
    (!isCodeRequired || !!mappedNomenclature.material_code)
  )
}

// Латинские буквы, неотличимые от кириллических: в сметах и НСИ их путают
// ("Раствор цементный М100" и "... M100", "A500C" и "А500С")
const LATIN_TO_CYRILLIC: Record<string, string> = {
  A: "А",
  B: "В",
  C: "С",
  E: "Е",
  H: "Н",
  K: "К",
  M: "М",
  O: "О",
  P: "Р",
  T: "Т",
  X: "Х",
  a: "а",
  c: "с",
  e: "е",
  o: "о",
  p: "р",
  x: "х",
  y: "у",
}
const LATIN_LOOKALIKES = new RegExp(`[${Object.keys(LATIN_TO_CYRILLIC).join("")}]`, "g")

// Слова и числа названия по порядку. Разделители между ними — пробел, точка,
// дефис, слэш — не важны, сами числа и их порядок важны
const NAME_TOKENS = /\p{N}+|\p{L}+/gu

const toComparableName = (name: string) =>
  (
    name
      .replace(LATIN_LOOKALIKES, (char) => LATIN_TO_CYRILLIC[char])
      .match(NAME_TOKENS) || []
  ).join(" ")

// Как привести название к виду для сравнения — по варианту из настройки
// (configuration.smr.sameAsNsiMode, KRE-762)
const TO_COMPARED_NAME: Record<SameAsNsiMode, (name: string) => string> = {
  // Посимвольно: латиница и кириллица, пробелы и разделители — разные символы
  // ("A500C" и "А500C", "ПБ 46 10-8" и "ПБ 46.10-8" не совпадают), регистр
  // тоже учитываем. Обрезаем только пробелы по краям
  strict: (name) => name.trim(),
  // - латиница и кириллица, которые на вид не отличить, одинаковы ("М100" и "M100");
  // - разделители не важны: "ПБ 46 10-8", "ПБ 46.10-8" и "ПБ46 10-8" совпадают;
  // - числа и их порядок важны: "ПБ 51.15-8" и "ПБ 51.5-8", "21x400x700" и
  //   "21x700x400" — разные; регистр тоже учитываем
  normalized: toComparableName,
}

// Название из сметы совпадает с найденным материалом НСИ (KRE-758). Как именно
// сравниваем — строго или с нормализацией, — задаёт настройка (KRE-762)
export const isSameAsMappedNomenclature = (
  mapping: WithId<MappingResult>,
  isCodeRequired?: boolean,
): boolean => {
  const toComparedName = TO_COMPARED_NAME[configuration.smr.sameAsNsiMode]
  const name = mapping.result?.nomenclature?.trim()
  const mappedName = getMappedNomenclature(mapping)?.nomenclature
  return (
    !!name &&
    !!mappedName &&
    hasMappedNomenclature(mapping, isCodeRequired) &&
    toComparedName(name) === toComparedName(mappedName)
  )
}
